import { format, subDays, parseISO, getDay, startOfWeek, addDays } from 'date-fns';

const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];

function targetDaysFor(habit) {
  const td = Array.isArray(habit?.targetDays) && habit.targetDays.length > 0 ? habit.targetDays : ALL_DAYS;
  return new Set(td);
}

function isTargetDay(habit, dateStr) {
  return targetDaysFor(habit).has(getDay(parseISO(dateStr)));
}

function todayStr(now = new Date()) {
  return format(now, 'yyyy-MM-dd');
}

// A frozen day counts exactly like a check-in for streak purposes — it keeps the
// streak alive and grants the same "today grace". This mirrors the server's
// calculateStreak (streaks.js), so the dashboard and server never disagree.
function protectedSet(checkinDates, freezeDates) {
  const set = new Set(checkinDates);
  for (const d of freezeDates || []) set.add(d);
  return set;
}

/**
 * Current streak: count back from today (or yesterday if today not yet done)
 * until we hit a target day that has no check-in (and no freeze).
 *
 * Non-target days are skipped — they don't break the streak.
 */
export function currentStreak(habit, checkinDates, now = new Date(), freezeDates = []) {
  const set = protectedSet(checkinDates, freezeDates);
  let streak = 0;
  let cursor = format(now, 'yyyy-MM-dd');
  const todayIsTarget = isTargetDay(habit, cursor);
  const todayDone = set.has(cursor);

  // Streak rule: a streak survives "today" if today is a target day but
  // hasn't been done yet (grace period). We walk back from today or yesterday.
  if (!todayDone && todayIsTarget) {
    // Don't count today as a miss yet — start counting from yesterday.
    cursor = format(subDays(now, 1), 'yyyy-MM-dd');
  }

  // Safety: never walk back more than 2 years.
  for (let i = 0; i < 730; i++) {
    if (isTargetDay(habit, cursor)) {
      if (set.has(cursor)) {
        streak++;
      } else {
        break;
      }
    }
    cursor = format(subDays(parseISO(cursor), 1), 'yyyy-MM-dd');
  }
  return streak;
}

export function bestStreak(habit, checkinDates, freezeDates = []) {
  if (!checkinDates || checkinDates.length === 0) return 0;
  const sorted = [...checkinDates].sort();
  // Walk forward day-by-day from the earliest check-in to today, counting
  // longest run of target-days-with-checkin (gaps only break the streak when
  // the gap day is a target day). Frozen days count like check-ins.
  const first = sorted[0];
  const last = todayStr();
  const set = protectedSet(sorted, freezeDates);
  let best = 0;
  let run = 0;
  let cursor = first;
  while (cursor <= last) {
    if (isTargetDay(habit, cursor)) {
      if (set.has(cursor)) {
        run++;
        if (run > best) best = run;
      } else {
        run = 0;
      }
    }
    cursor = format(addDays(parseISO(cursor), 1), 'yyyy-MM-dd');
    // Guard.
    if (cursor.length !== 10) break;
  }
  return best;
}

export function totalCompletions(checkinDates) {
  return checkinDates?.length ?? 0;
}

/**
 * Completion rate over the last N days, considering only target days.
 * Returns 0..100.
 */
export function completionRate(habit, checkinDates, days = 30, now = new Date()) {
  const set = new Set(checkinDates);
  let target = 0;
  let done = 0;
  for (let i = 0; i < days; i++) {
    const d = format(subDays(now, i), 'yyyy-MM-dd');
    if (isTargetDay(habit, d)) {
      target++;
      if (set.has(d)) done++;
    }
  }
  if (target === 0) return 0;
  return Math.round((done / target) * 100);
}

/**
 * Last 12 weeks of completion rates (oldest → newest).
 */
export function weeklyRates(habit, checkinDates, weeks = 12, now = new Date()) {
  const set = new Set(checkinDates);
  const out = [];
  const currentWeekStart = startOfWeek(now, { weekStartsOn: 1 });
  for (let w = weeks - 1; w >= 0; w--) {
    const weekStart = subDays(currentWeekStart, w * 7);
    let target = 0;
    let done = 0;
    for (let i = 0; i < 7; i++) {
      const d = format(addDays(weekStart, i), 'yyyy-MM-dd');
      if (isTargetDay(habit, d)) {
        target++;
        if (set.has(d)) done++;
      }
    }
    out.push({ weekStart: format(weekStart, 'yyyy-MM-dd'), rate: target === 0 ? 0 : Math.round((done / target) * 100) });
  }
  return out;
}

/**
 * Histogram of check-ins by day of week (Sun=0..Sat=6).
 */
export function dayOfWeekCounts(checkinDates) {
  const counts = [0, 0, 0, 0, 0, 0, 0];
  for (const d of checkinDates ?? []) {
    const dow = getDay(parseISO(d));
    counts[dow]++;
  }
  return counts;
}

/**
 * Per-habit summary used by Dashboard + HabitDetail.
 */
export function summarizeHabit(habit, checkinDates, now = new Date(), freezeDates = []) {
  return {
    // Streaks respect freezes; completion metrics count only real check-ins.
    currentStreak: currentStreak(habit, checkinDates, now, freezeDates),
    bestStreak: bestStreak(habit, checkinDates, freezeDates),
    totalCompletions: totalCompletions(checkinDates),
    completionRate30d: completionRate(habit, checkinDates, 30, now),
    weeklyRates: weeklyRates(habit, checkinDates, 12, now),
    dayOfWeekCounts: dayOfWeekCounts(checkinDates),
  };
}

/**
 * App-wide momentum score, 0..100. Weighted blend:
 *   50% today's completion rate
 *   30% average current streak, scaled (30 days = 100%)
 *   20% last-7-day completion rate
 *
 * Must stay in lockstep with the server formula in
 * `amplify/backend/function/01habittrackerstreak4319170d/src/index.js`
 * (statsOverview) so /stats/overview and the dashboard never disagree.
 */
export function momentumScore(habits, checkinsByHabit, now = new Date(), freezesByHabit = {}) {
  if (!habits || habits.length === 0) return 0;
  const today = format(now, 'yyyy-MM-dd');

  let todayTotal = 0;
  let todayDone = 0;
  let weekTotal = 0;
  let weekDone = 0;
  let streakSum = 0;

  for (const h of habits) {
    const dates = checkinsByHabit[h.habitId] ?? [];
    const freezes = freezesByHabit[h.habitId] ?? [];
    // Completion rates count only real check-ins (matches server statsOverview);
    // the streak component respects freezes.
    const set = new Set(dates);
    if (isTargetDay(h, today)) {
      todayTotal++;
      if (set.has(today)) todayDone++;
    }
    for (let i = 0; i < 7; i++) {
      const d = format(subDays(now, i), 'yyyy-MM-dd');
      if (isTargetDay(h, d)) {
        weekTotal++;
        if (set.has(d)) weekDone++;
      }
    }
    streakSum += currentStreak(h, dates, now, freezes);
  }

  const todayRate = todayTotal > 0 ? todayDone / todayTotal : 0;
  const weekRate = weekTotal > 0 ? weekDone / weekTotal : 0;
  const avgStreak = habits.length > 0 ? streakSum / habits.length : 0;
  const streakScore = Math.min(avgStreak / 30, 1);
  return Math.round(todayRate * 50 + streakScore * 30 + weekRate * 20);
}

/**
 * Group an array of checkin rows (from checkinsRepo) by habitId → date[].
 */
export function groupCheckinsByHabit(checkinRows) {
  const map = {};
  for (const c of checkinRows ?? []) {
    if (!map[c.habitId]) map[c.habitId] = [];
    map[c.habitId].push(c.date);
  }
  for (const k of Object.keys(map)) map[k].sort();
  return map;
}

/**
 * Identify habits whose current streak is at risk: free users, target day,
 * 7+ day current streak, not yet checked in today. Used for the "streak at
 * risk" modal on Dashboard.
 */
export function streakAtRiskHabits(habits, checkinsByHabit, now = new Date(), freezesByHabit = {}) {
  const today = format(now, 'yyyy-MM-dd');
  const out = [];
  for (const h of habits) {
    if (!isTargetDay(h, today)) continue;
    const dates = checkinsByHabit[h.habitId] ?? [];
    const freezes = freezesByHabit[h.habitId] ?? [];
    if (dates.includes(today)) continue;
    // Already protected today by a freeze — not at risk.
    if (freezes.includes(today)) continue;
    const cs = currentStreak(h, dates, now, freezes);
    if (cs >= 7) {
      out.push({ ...h, currentStreak: cs, bestStreak: bestStreak(h, dates, freezes) });
    }
  }
  return out;
}
